import { ApolloError, useQuery, useReactiveVar } from '@apollo/client';
import AccountContext from 'context/AccountContext';
import { GET_LTP_PARK_ZONES } from 'graphql/queries/getLTPParkZones';
import { useContext, useMemo } from 'react';
import { LatLng } from 'react-native-maps';
import { featureCountryCode } from 'states/persistInStorage';
import {
  CountryCode,
  Maybe,
  ParkZone,
  Query,
  QueryParkZonesWithLtpArgs,
} from 'types/generatedSchemaTypes';
import { getZonesWithVisibility } from 'utils/parkUtils';

interface UseLTPParkZonesReturn {
  parkingZonesData: ParkZone[];
  loading: boolean;
  error?: ApolloError;
}

export const useGetParkingZonesWithBuyablePermits = (
  currentCountry: Maybe<CountryCode>,
  enabled: boolean,
  // The zones are ordered by the distance from this location (the query itself has no location)
  location: LatLng | null = null
): UseLTPParkZonesReturn => {
  const {
    customer: { householdBenefits },
  } = useContext(AccountContext);
  const appCountry = useReactiveVar(featureCountryCode);
  // Only used to put the household benefit zones of this country first. The query has no country,
  // so the list has the zones of every country.
  const currentAppCountry = currentCountry ?? appCountry;
  const { data, loading, error } = useQuery<Query, QueryParkZonesWithLtpArgs>(
    GET_LTP_PARK_ZONES,
    {
      fetchPolicy: 'cache-and-network',
      skip: !enabled,
    }
  );

  const parkingZonesData = useMemo(
    () =>
      getZonesWithVisibility(
        (data?.parkZonesWithLTP?.content ?? []) as ParkZone[],
        data?.parkZonesWithLTP?.pmcVisibilities,
        householdBenefits,
        location,
        currentAppCountry,
        false
      ),
    [
      data?.parkZonesWithLTP?.content,
      data?.parkZonesWithLTP?.pmcVisibilities,
      householdBenefits,
      location,
      currentAppCountry,
    ]
  );
  return { parkingZonesData, loading, error };
};
